import React, { useEffect, useMemo, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import DashboardService from '../../../Patient/Dashboard/services/Service';
import Service from '../../Dashboard/services/Service';
import AdminRoleReport from '../../../Patient/Reports/components/AdminRoleReport/AdminRoleReport';
import EmbeddedReport from './EmbeddedReport';
import '../../Dashboard/styles/Dashboard.css'; // Import admin dropdown styles

const AdminReport = () => {
  // const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [patientQuery, setPatientQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientOpen, setIsPatientOpen] = useState(false);

  const [orders, setOrders] = useState([]);
  const [orderQuery, setOrderQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderOpen, setIsOrderOpen] = useState(false);

  // Fetch first page of patients for selection (admin list)
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        // Load first page with reasonable size
        const res = await Service.getPatientList();
        const results = res?.data?.results || res?.data || [];
        setPatients(Array.isArray(results) ? results : []);
      } catch (e) {
        setPatients([]);
      }
    };
    fetchPatients();
  }, []);

  // When a patient is selected, fetch orders (will use cache if available)
  useEffect(() => {
    const fetchOrders = async () => {
      if (!selectedPatient?.id) {
        setOrders([]);
        setSelectedOrder(null);
        return;
      }
      // setOrdersLoading(true);
      try {
        const res = await DashboardService.getOrderList(selectedPatient.id);
        const list = Array.isArray(res?.data) ? res.data : [];
        setOrders(list);
      } catch (e) {
        setOrders([]);
      }
    };
    fetchOrders();
  }, [selectedPatient?.id]);

  const filteredPatients = useMemo(() => {
    const list = Array.isArray(patients) ? patients : [];
    const q = patientQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const name = `${p?.first_name || ''} ${p?.last_name || ''}`.trim().toLowerCase();
      const email = (p?.email || '').toLowerCase();
      const username = (p?.username || '').toLowerCase();
      return name.includes(q) || email.includes(q) || username.includes(q);
    });
  }, [patientQuery, patients]);

  const filteredOrders = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    const q = orderQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((o) => {
      const num = String(o?.order_number || '').toLowerCase();
      const status = (o?.db_order?.status_detail?.label || '').toLowerCase();
      return num.includes(q) || status.includes(q);
    });
  }, [orderQuery, orders]);

  const [showReport, setShowReport] = useState(false);

  const handleViewReport = () => {
    if (!selectedPatient?.id || !selectedOrder?.id) return;
    setShowReport(true);
  };

  const displayName = `${selectedPatient?.first_name || 'User'}${selectedPatient?.last_name ? ' ' + selectedPatient.last_name : ''}`;

  return (
    <div className="report-container" style={{ maxWidth: 900, margin: '0 auto' }}>
      <h1 className="report-title">Admin Report</h1>

      {/* Patient selector (custom dropdown) */}
      <div className="user-dropdown-section" style={{ marginBottom: 20 }}>
        <label>Select a Patient to view dashboard:</label>

        <div className="admin-user-dropdown">
          {/* Dropdown Header */}
          <div className="admin-dropdown-header" onClick={() => setIsPatientOpen((prev) => !prev)}>
            <span>{selectedPatient ? `${selectedPatient.first_name || 'No Name'} — ${selectedPatient.email}` : '-- Select User --'}</span>
            <span className="admin-dropdown-arrow">{isPatientOpen ? '▲' : '▼'}</span>
          </div>

          {/* Dropdown Body */}
          {isPatientOpen && (
            <div className="admin-dropdown-body">
              {/* Search Box */}
              <input
                type="text"
                placeholder="Search user..."
                className="admin-dropdown-search"
                value={patientQuery}
                onChange={(e) => setPatientQuery(e.target.value)}
              />

              {/* List */}
              <div className="admin-dropdown-list">
                {Array.isArray(filteredPatients) && filteredPatients.length > 0 ? (
                  filteredPatients.map((p) => (
                    <div
                      key={p.id}
                      className="admin-dropdown-item"
                      onClick={() => {
                        setSelectedPatient(p);
                        setIsPatientOpen(false);
                        setSelectedOrder(null);
                        setOrderQuery('');
                      }}
                    >
                      {(p.first_name || 'No Name') + (p.last_name ? ` ${p.last_name}` : '')} — {p.email}
                    </div>
                  ))
                ) : (
                  <div className="admin-dropdown-item no-results">No Users Found</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order selector (custom dropdown) */}
      <div className="user-dropdown-section" style={{ marginBottom: 20 }}>
        <label className="dropdown-label">Select order</label>

        <div className="admin-user-dropdown">
          {/* Dropdown Header */}
          <div className="admin-dropdown-header" onClick={() => selectedPatient && setIsOrderOpen((prev) => !prev)}>
            <span>
              {selectedOrder
                ? `#${selectedOrder.order_number} — ${selectedOrder?.db_order?.status_detail?.label || 'Processing'}`
                : '-- Select Order --'}
            </span>
            <span className="admin-dropdown-arrow">{isOrderOpen ? '▲' : '▼'}</span>
          </div>

          {/* Dropdown Body */}
          {isOrderOpen && (
            <div className="admin-dropdown-body">
              {/* Search Box */}
              <input
                type="text"
                placeholder="Search by order # or status"
                className="admin-dropdown-search"
                value={orderQuery}
                onChange={(e) => setOrderQuery(e.target.value)}
                disabled={!selectedPatient}
              />

              {/* List */}
              <div className="admin-dropdown-list">
                {Array.isArray(filteredOrders) && filteredOrders.length > 0 ? (
                  filteredOrders.map((o) => (
                    <div
                      key={o.id}
                      className="admin-dropdown-item"
                      onClick={() => {
                        setSelectedOrder(o);
                        setIsOrderOpen(false);
                      }}
                    >
                      #{o.order_number} — {o?.db_order?.status_detail?.label || 'Processing'}
                    </div>
                  ))
                ) : (
                  <div className="admin-dropdown-item no-results">No Orders Found</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={handleViewReport}
        className="back-button"
        disabled={!selectedPatient || !selectedOrder}
        style={{ opacity: !selectedPatient || !selectedOrder ? 0.5 : 1 }}
      >
        View Report
      </button>

      {showReport && selectedOrder && (
        <div style={{ marginTop: 24 }}>
          <div style={{ marginTop: 24 }}>
            <EmbeddedReport selectedOrderDetails={selectedOrder} displayName={displayName} />
          </div>
          <AdminRoleReport
            orders={orders}
            loading={false}
            selectedOrder={String(selectedOrder.order_number)}
            selectedOrderDetails={selectedOrder}
            handleOrderChange={() => {}}
            onBack={() => setShowReport(false)}
          />
        </div>
      )}
    </div>
  );
};

export default AdminReport;
